import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import moment from 'moment'

import PickDate from './PickDate'
import PickTime from './PickTime'
import getOpenings from '@utils/getOpenings'
import submitHealthyHomeLead from '@utils/submitHealthyHomeLead'
import { useFormContext } from '@context/FormContext'
import Modal from '@components/ui/Modal'
import Button from '@components/ui/Button'

import urlPathSetter from '@utils/urlPathSetter'

const Schedule = ({
  currentStepIndex,
  setValidationSchema,
  setEnableSubmitButton,
}) => {
  const { values } = useFormContext()

  if (!values) return null

  values.step = 'schedule'
  urlPathSetter(values)
  const [openings, setOpenings] = useState([])
  const [disabledDays, setDisabledDays] = useState([])
  const [startDay, setStartDay] = useState(new Date())
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState(undefined)
  const [errorMessage, setErrorMessage] = useState(undefined)
  const [noSlotsModalOpen, setNoSlotsModalOpen] = useState(false)
  const [isSubmittingLead, setIsSubmittingLead] = useState(false)
  const hasFetchedOnce = useRef(false)
  const { path } = values || {}

  const handleGetOpenings = async (newDate) => {
    const address = values?.contactInfo?.address
    let program = values?.gasProvider
      ? values.gasProvider.replace(' ', '')
      : values?.electricProvider

    const monthEnd = moment(startDay).endOf('month')
    setIsLoading(true)
    setErrorMessage(undefined)

    try {
      const openings = await getOpenings(
        newDate,
        monthEnd,
        address,
        program,
        values
      )
      setOpenings(openings)
      if (path === 'healthyhome' && openings.length === 0 && hasFetchedOnce.current) {
        setNoSlotsModalOpen(true)
      }
    } catch (error) {
      console.error('[SCHEDULE] Error fetching openings:', error)
      setErrorMessage(error.message)
      if (path === 'healthyhome') {
        setNoSlotsModalOpen(true)
      }
    }

    setIsLoading(false)
    hasFetchedOnce.current = true
  }

  const handleDisabledDays = () => {
    const dateFormat = 'YYYY-MM-DD'
    const daysOfMonth = {}
    const currentDay = moment(startDay)
    const lastDay = moment(startDay).endOf('month')
    while (currentDay.isSameOrBefore(lastDay)) {
      daysOfMonth[currentDay.format(dateFormat)] = moment(currentDay).toDate()
      currentDay.add(1, 'Days')
    }
    const daysWithOpenings = []
    for (let i = 0; i < openings.length; i++) {
      const dateKey = moment(openings[i].startTimeISO).format(dateFormat)
      if (!daysWithOpenings.includes(dateKey)) daysWithOpenings.push(dateKey)
    }
    daysWithOpenings.forEach((day) => {
      delete daysOfMonth[day]
    })
    setDisabledDays(Object.values(daysOfMonth))
  }

  useEffect(() => {
    setValidationSchema(
      yup.object().shape({
        schedule: yup.string().required('This field is required'),
      })
    )
  }, [currentStepIndex])

  useEffect(() => {
    if (values?.contactInfo?.address) {
      handleGetOpenings(startDay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDay, values?.contactInfo?.address, values?.gasProvider, values?.electricProvider])

  useEffect(() => {
    handleDisabledDays()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openings, startDay])

  useEffect(() => {
    if (selectedDate == null) setEnableSubmitButton(false)
    else setEnableSubmitButton(true)
  }, [selectedDate])

  const isCapAgency =
    values?.paymentAssistance === 'yes' && values?.contactInfo?.source === 'CAP'
  const isCap = values?.paymentAssistance === 'yes'
  const isSingleFamilyMarketRate =
    values?.paymentAssistance === 'no' &&
    values?.singleOrMulti === 'Single family'

  return (
    <div className="max-w-2xl mx-auto px-4">

      {/* Header */}
      <div
        className="text-center mb-6"
        style={{ animation: 'fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both' }}
      >
        <p
          className="text-xs font-semibold uppercase tracking-widest mb-2"
          style={{ color: '#4675ad', letterSpacing: '0.1em' }}
        >
          Almost Done
        </p>
        <h1
          className="text-3xl md:text-4xl text-stone-900 leading-tight mb-3"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 }}
        >
          Choose a time that works for you
        </h1>
        <p className="text-stone-500 text-sm">
          {`We estimate your ${path === 'hea' ? 'energy assessment' : 'appointment'} will take ${path === 'healthyhome' ? 'up to 2 hours' : '2–3 hours'}. Pick a date and arrival window — our team will confirm within 24 hours.`}
        </p>
      </div>

      {/* Calendar + Time slots grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Calendar column */}
        <div
          className="bg-white rounded-2xl p-5"
          style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
        >
          <PickDate
            monthStart={startDay}
            handleDateChange={setSelectedDate}
            handleMonthChange={setStartDay}
            disabledDays={disabledDays}
            isLoading={isLoading}
            isCap={isCap}
            isCapAgency={isCapAgency}
            isSingleFamilyMarketRate={isSingleFamilyMarketRate}
            path={path}
          />
        </div>

        {/* Time slots column */}
        <div
          className="bg-white rounded-2xl p-5"
          style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
        >
          {!selectedDate && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="mb-3 opacity-30">
                <rect x="5" y="8" width="30" height="27" rx="4" stroke="#78716c" strokeWidth="2" />
                <path d="M5 16h30" stroke="#78716c" strokeWidth="2" />
                <path d="M13 4v6M27 4v6" stroke="#78716c" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p className="text-stone-400 text-sm">Select a date to see available times</p>
            </div>
          )}
          {selectedDate && (
            <div>
              <h3 className="font-semibold text-stone-800 text-sm mb-4">Select an arrival window</h3>
              <PickTime
                errorMessage={errorMessage}
                openings={openings}
                selectedDate={selectedDate}
              />
            </div>
          )}
        </div>

      </div>

      {noSlotsModalOpen && (
        <Modal
          initialOpen
          dismissable={false}
          title="We'll be there soon!"
          body={() => (
            <p className="text-center text-stone-600">
              We&apos;re expanding to your area soon. Please check back later.
            </p>
          )}
          footer={() => (
            <Button
              disabled={isSubmittingLead}
              onClick={async () => {
                setIsSubmittingLead(true)
                try {
                  await submitHealthyHomeLead(values)
                } catch (error) {
                  console.error('[SCHEDULE] Failed to create HealthyHome lead:', error)
                }
                window.location.href = 'https://homeworksenergy.com'
              }}
            >
              {isSubmittingLead ? 'Sending...' : 'OK'}
            </Button>
          )}
        />
      )}
    </div>
  )
}

Schedule.info = {
  title: 'Schedule',
  key: 'schedule',
  isSubmitStep: true,
}

Schedule.propTypes = {
  currentStepIndex: PropTypes.number.isRequired,
  setValidationSchema: PropTypes.func.isRequired,
  setEnableSubmitButton: PropTypes.func.isRequired,
}

export default Schedule
